<template>
  <PTransparentLoader :loading="loading">
    <CRow v-if="chargeback">
      <CCol lg="12">
        <div class="d-flex align-items-center mb-3">

          <CLink
            style="color: #100F23"
            class="mr-4"
            @click="goBack"
          >
            <CIcon size="custom-size" :width="18" name="cipArrowNarrowLeft" class="mr-2"/>
            <translate translate-context="accounting.order">Back</translate>
          </CLink>
          <h1 class="main-header mb-0">
            <translate translate-context="accounting.orderDetail">Order</translate>
            {{ dispute.order_number || chargeback.id }}
          </h1>
        </div>


        <CCard class="mb-3">
          <CCardHeader class="border-0 px-4 pb-1 pt-4">
            <div class="d-flex align-items-center font-weight-medium">
              <CIcon name="cipShoppingBag01" size="custom-size" :width="18" class="mr-2"/>
              <translate translate-context="accounting.movement.refund">Order information</translate>
            </div>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol md="4">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Order Number</translate>
                  </div>
                  <CLink
                    class="font-weight-medium"
                    v-if="dispute.order_id && orderRoute"
                    :to="orderRoute"
                  >
                    {{ dispute.order_number }}
                  </CLink>
                  <div v-else class="font-weight-medium">{{ dispute.order_number || '-' }}</div>
                </div>
              </CCol>
              <CCol md="4">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Event</translate>
                  </div>
                  <div class="font-weight-medium">{{ chargeback.pica_service_name || '-' }}</div>
                </div>
              </CCol>
            </CRow>
            <CRow>
              <CCol md="4">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Effective Date</translate>
                  </div>
                  <div class="font-weight-medium">{{ dispute.effective_date|formatDateTime('L LT') }}</div>
                </div>
              </CCol>
              <CCol md="4" v-if="hasScopePerm('accounting.chargeback.fields.user_email')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">User Email</translate>
                  </div>
                  <div class="font-weight-medium">{{ dispute.user_email || '-' }}</div>
                </div>
              </CCol>
              <CCol md="4" v-if="dispute.chargeback_type && hasScopePerm('accounting.chargeback.fields.chargeback_type')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Dispute Type</translate>
                  </div>
                  <CBadge color="primary" class="badge-outline">{{ dispute.chargeback_type.name }}</CBadge>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>


        <CCard class="mb-3" v-if="hasAnyDisputeField">
          <CCardHeader class="border-0 px-4 pb-1 pt-4">
            <div class="d-flex align-items-center font-weight-medium">
              <CIcon name="cipInfo" size="custom-size" :width="18" class="mr-2"/>
              <translate translate-context="chargeback.detail">Dispute Info</translate>
            </div>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol md="4" v-if="hasScopePerm('accounting.chargeback.fields.dispute_status')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Dispute Status</translate>
                  </div>
                  <CBadge v-if="dispute.dispute_status" :color="getDisputeStatusColor(dispute.dispute_status)" class="badge-outline">
                    {{ getDisputeStatusLabel(dispute.dispute_status) }}
                  </CBadge>
                  <span v-else class="text-muted">-</span>
                </div>
              </CCol>
              <CCol md="4" v-if="hasScopePerm('accounting.chargeback.fields.dispute_opened_at')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Dispute Opened</translate>
                  </div>
                  <div class="font-weight-medium">
                    <span v-if="dispute.dispute_opened_at">{{ dispute.dispute_opened_at|formatDateTime('L LT') }}</span>
                    <span v-else class="text-muted">-</span>
                  </div>
                </div>
              </CCol>
              <CCol md="4" v-if="hasScopePerm('accounting.chargeback.fields.dispute_resolved_at')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Dispute Resolved</translate>
                  </div>
                  <div class="font-weight-medium">
                    <span v-if="dispute.dispute_resolved_at">{{ dispute.dispute_resolved_at|formatDateTime('L LT') }}</span>
                    <span v-else class="text-muted">-</span>
                  </div>
                </div>
              </CCol>
            </CRow>
            <CRow v-if="hasScopePerm('accounting.chargeback.fields.dispute_status_changed_at') || hasScopePerm('accounting.chargeback.fields.created_by_email')">
              <CCol md="4" v-if="hasScopePerm('accounting.chargeback.fields.dispute_status_changed_at')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Status Changed</translate>
                  </div>
                  <div class="font-weight-medium">
                    <span v-if="dispute.dispute_status_changed_at">{{ dispute.dispute_status_changed_at|formatDateTime('L LT') }}</span>
                    <span v-else class="text-muted">-</span>
                  </div>
                </div>
              </CCol>
              <CCol md="4" v-if="hasScopePerm('accounting.chargeback.fields.created_by_email')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Created by</translate>
                  </div>
                  <div class="font-weight-medium">
                    {{ dispute.created_by_email || '-' }}
                  </div>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- Current record + dispute-wide aggregates -->
        <ChargebackAmountsWaterfall
          v-if="hasAnyAmountField && (hasRecordAmounts || hasAnyGlobalAmount)"
          :record="chargeback"
        />

        <!-- Chargeback ledger entries (reversal entries) for this record -->
        <ChargebackLedgerEntries
          v-if="hasEntries"
          :record="chargeback"
          @open-explanation="openExplanation"
        />
        <ChargebackLedgerExplanationModal ref="explanationModal"/>

        <!-- Stripe Info (always visible, last block) -->
        <CCard class="mb-3" v-if="stripeRaw && hasScopePerm('accounting.chargeback.fields.stripe_debug')">
          <CCardHeader class="border-0 px-4 pb-1 pt-4">
            <div class="d-flex align-items-center font-weight-medium">
              <CIcon name="cipCreditCard02" size="custom-size" :width="18" class="mr-2"/>
              <translate translate-context="chargeback.detail">Stripe info</translate>
            </div>
          </CCardHeader>
          <CCardBody>
            <div v-if="dispute.reason && hasScopePerm('accounting.chargeback.fields.reason')" class="mb-3">
              <div class="info-label">
                <translate translate-context="chargeback.detail">Notes</translate>
              </div>
              <div class="info-cell-highlight">
                {{ dispute.reason }}
              </div>
            </div>
            <pre class="stripe-raw-json">{{ stripeRawPretty }}</pre>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </PTransparentLoader>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import ChargebackAmountsWaterfall from './components/ChargebackAmountsWaterfall.vue'
import ChargebackLedgerEntries from './components/ChargebackLedgerEntries.vue'
import ChargebackLedgerExplanationModal from './components/ChargebackLedgerExplanationModal.vue'
import {getDetailRouteParams, getPermissionPrefix, TRANSACTION_TYPES} from '../constants'
import {DISPUTE_STATUS_LABELS, DISPUTE_STATUS_COLORS} from './consts'
import ConfigScope from "@/domain/core/constant/configScope"
export default {
  name: 'ChargebackDetail',
  components: {
    PTransparentLoader,
    ChargebackAmountsWaterfall,
    ChargebackLedgerEntries,
    ChargebackLedgerExplanationModal,
  },
  data() {
    return {
      loading: true
    }
  },
  async mounted() {
    await this.setScope(this.scope)
    await this.fetchScopedConfig()
    await this.loadChargeback()
  },
  computed: {
    ...mapGetters('accounting/chargeback', ['chargeback']),

    dispute() {
      return this.chargeback?.dispute || {}
    },

    recordAmounts() {
      return this.chargeback?.record || {}
    },

    scopeType() {
      return this.$route.meta.scopeType || 'global'
    },

    scopeId() {
      if (this.scopeType === 'pica-service') {
        return this.$route.params.picaServiceId
      } else if (this.scopeType === 'contract-manager') {
        return this.$route.params.contractManagerId
      }
      return null
    },

    scope() {
      return {
        type: this.scopeType,
        id: this.scopeId ? parseInt(this.scopeId) : null
      }
    },

    listRouteName() {
      if (this.scopeType === 'contract-manager') return 'contractManager.chargebacks'
      if (this.scopeType === 'pica-service') return 'event.chargebacks'
      return 'admin.chargebacks'
    },

    permissionPrefix() {
      return getPermissionPrefix(this.scopeType)
    },

    orderRoute() {
      if (!this.dispute.order_id) return null
      if (this.scope.type !== 'global') return null
      if (!this.hasPerm(`${this.permissionPrefix}.accounting.order.detail`)) return null
      return getDetailRouteParams(TRANSACTION_TYPES.ORDER, this.dispute.order_id, this.scope)
    },

    hasAnyDisputeField() {
      return this.hasScopePerm('accounting.chargeback.fields.dispute_status')
        || this.hasScopePerm('accounting.chargeback.fields.dispute_opened_at')
        || this.hasScopePerm('accounting.chargeback.fields.dispute_resolved_at')
        || this.hasScopePerm('accounting.chargeback.fields.dispute_status_changed_at')
    },

    amountsPermissions() {
      return {
        chargebackAmount: this.hasScopePerm('accounting.chargeback.fields.chargeback_amount'),
        disputeFee: this.hasScopePerm('accounting.chargeback.fields.dispute_fee_amount'),
        totalLoss: this.hasScopePerm('accounting.chargeback.fields.total_loss'),
        photographerLoss: this.hasScopePerm('accounting.chargeback.fields.photographer_loss'),
        picaLoss: this.hasScopePerm('accounting.chargeback.fields.pica_loss'),
        organizerLoss: this.hasScopePerm('accounting.chargeback.fields.organizer_loss'),
      }
    },

    hasAnyAmountField() {
      return Object.values(this.amountsPermissions).some(Boolean)
    },

    hasAnyGlobalAmount() {
      const d = this.chargeback?.dispute
      if (!d) return false
      return parseFloat(d.total_chargeback_amount)
        || parseFloat(d.total_dispute_fee)
        || parseFloat(d.total_loss)
        || parseFloat(d.photographer_loss)
        || parseFloat(d.pica_loss)
        || parseFloat(d.organizer_loss)
    },

    hasEntries() {
      return !!(this.chargeback && this.chargeback.entries && this.chargeback.entries.length)
    },

    hasRecordAmounts() {
      const r = this.recordAmounts
      return parseFloat(r.chargeback_amount)
        || parseFloat(r.dispute_fee)
        || parseFloat(r.total_loss)
        || parseFloat(r.photographer_loss)
        || parseFloat(r.pica_loss)
        || parseFloat(r.organizer_loss)
    },

    stripeRaw() {
      return this.dispute.context?.stripe_raw || null
    },

    stripeRawPretty() {
      return this.stripeRaw ? JSON.stringify(this.stripeRaw, null, 2) : ''
    },
  },
  methods: {
    ...mapActions('accounting', ['setScope']),
    ...mapActions('accounting/chargeback', ['fetchChargeback']),
    ...mapActions('login', ['fetchConfig']),

    openExplanation() {
      this.$refs.explanationModal.open()
    },

    hasScopePerm(permission) {
      if (!this.scope || !this.scope.type) return false

      let fullPermission
      if (this.scope.type === 'pica-service') {
        fullPermission = `pica_service.${permission}`
      } else if (this.scope.type === 'contract-manager') {
        fullPermission = `contract_manager.${permission}`
      } else if (this.scope.type === 'global') {
        fullPermission = `admin.${permission}`
      } else {
        return false
      }

      return this.hasPerm(fullPermission)
    },

    async fetchScopedConfig() {
      try {
        if (this.scopeType === 'pica-service') {
          await this.fetchConfig({
            type: 'accounting_pica_service',
            id: this.scopeId,
            scope: ConfigScope.PICA_SERVICE
          })
        } else if (this.scopeType === 'contract-manager') {
          await this.fetchConfig({
            type: 'accounting_contract_manager',
            id: this.scopeId
          })
        } else {
          await this.fetchConfig({
            type: 'accounting_global'
          })
        }
      } catch (e) {
        // Config endpoint may not exist yet
      }
    },

    goBack() {
      const params = {}
      if (this.scopeType === 'contract-manager' && this.scopeId) {
        params.contractManagerId = this.scopeId
      } else if (this.scopeType === 'pica-service' && this.scopeId) {
        params.picaServiceId = this.scopeId
      }
      this.$router.push({name: this.listRouteName, params})
    },

    async loadChargeback() {
      this.loading = true
      try {
        await this.fetchChargeback(this.$route.params.id)
      } finally {
        this.loading = false
      }
    },

    getDisputeStatusLabel(status) {
      return DISPUTE_STATUS_LABELS[status] || status
    },

    getDisputeStatusColor(status) {
      return DISPUTE_STATUS_COLORS[status] || 'secondary'
    },
  }
}
</script>

<style scoped>
.stripe-raw-json {
  background-color: #f8f9fa;
  border: 1px solid #ebedef;
  border-radius: 4px;
  padding: 12px;
  font-size: 12px;
  max-height: 600px;
  overflow: auto;
  margin: 0;
  white-space: pre;
}
</style>
